import { useState, useEffect, useCallback, useMemo } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import parse from "html-react-parser";
import toast from "react-hot-toast";
import {
  MapPin, Landmark, Calendar, Clock, Info, ChevronLeft, ChevronRight,
  Minus, Plus, Zap,
} from "lucide-react";
import {
  BACKEND_API_ROUTE,
  TEMPLE_PUJA_API,
  TEMPLE_PUJA_PACKAGES_API,
  TEMPLE_PUJA_DATE_SLOTS_API,
} from "@/commons/api_routes";
import { GETREQUESTAPI } from "@/pages/api/apiUtils";
import { parsePoojaDescriptionRawHTML, getCookieItem, handleGuestLoginFlow, formatAmountPutComma } from "@/utils/utils";
import { DEFAULT_SEO_VALUES, TEMPLE_PUJA_HIDE_CUSTOM_TIER } from "@/commons/constants";
import TemplePujaCheckout from "@/components/bookingComponent/TemplePujaCheckout";
import ImageWrapper from "@/components/ImageComponent/ImageWrapper";

const PARTICIPANT_TIERS = ["individual", "couple", "family", "largeFamily", "custom"];
// Tiers actually shown to devotees — respects the hide-custom-tier flag above
// without touching the admin package editor, which still manages all 5.
const VISIBLE_PARTICIPANT_TIERS = PARTICIPANT_TIERS.filter(
  (k) => !(TEMPLE_PUJA_HIDE_CUSTOM_TIER && k === "custom")
);
const TIER_LABELS = {
  individual: "Individual",
  couple: "Couple",
  family: "Family",
  largeFamily: "Large Family",
  custom: "Custom",
};
// Fixed-tier field name (camelCase) -> wire enum value (snake_case) expected
// by POST /temple-puja-orders' `packageType`.
const PACKAGE_TYPE_MAP = {
  individual: "individual",
  couple: "couple",
  family: "family",
  largeFamily: "large_family",
  custom: "custom",
};

// Background color per tier, keyed the same way PoojaPackageComponent keys
// its Basic/Standard/Premium cards by name.
const TIER_BG_CLASS = {
  individual: "bg-orange-50",
  couple: "bg-red-50",
  family: "bg-red-100",
  largeFamily: "bg-orange-100",
  custom: "bg-rose-50",
};

const tierExists = (tier) => !!tier && Number(tier.minParticipants) > 0;

// India has a fixed UTC+5:30 offset (no DST), so wall-clock IST instants can
// be compared reliably by shifting both sides onto the same "UTC-interpreted"
// number line — no timezone library or Intl parsing quirks needed.
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

// slot.date is "DD-MM-YYYY", slot.timeSlot/timeSlot is "HH:mm" (24h) — both
// wall-clock IST, matching the backend's temple-puja-date-slots schema.
const parseISTSlotToMillis = (dateStr, timeStr) => {
  const [day, month, year] = (dateStr || "").split("-").map(Number);
  const [hour, minute] = (timeStr || "00:00").split(":").map(Number);
  if (!day || !month || !year) return null;
  return Date.UTC(year, month - 1, day, hour || 0, minute || 0);
};

const getNowInISTMillis = () => Date.now() + IST_OFFSET_MS;

// ── Package tier card — styled to match PoojaPackageComponent (regular Pooja) ──
function TemplePujaPackageCard({
  tierKey,
  tier,
  isSelected,
  participantCount,
  isBookingThisTier,
  onSelectCustomTier,
  onCustomCountChange,
  onBookNow,
}) {
  const isCustom = tierKey === "custom";

  return (
    <div className="mx-2 relative">
      <div className="flex flex-row z-10 justify-center w-full absolute -top-5 h-24">
        <ImageWrapper
          src="/icons/Group6903.svg"
          alt="Line design"
          width={112}
          height={48}
          className="object-contain"
        />
      </div>

      <div
        className={`w-[260px] pb-4 border ${isSelected ? "border-BluntRed ring-2 ring-BluntRed" : "border-red-100"} flex-none shadow-lg px-3 rounded-md ${TIER_BG_CLASS[tierKey] || ""} overflow-hidden relative`}
      >
        <div className="relative w-20 h-12 absolute -top-7 -right-9 overflow-hidden">
          <ImageWrapper
            src="/icons/Group6334.svg"
            alt="Design image"
            fill
            className="object-cover"
          />
        </div>

        <div className="pt-0">
          <div className="flex items-center justify-between text-sm font-lato border-b border-dashed pb-1">
            <span className="text-gray-700 tracking-wide">{TIER_LABELS[tierKey]}</span>
            <span className="text-BluntRed font-bold text-base">
              ₹ {formatAmountPutComma(tier.price)}
            </span>
          </div>
        </div>

        <div className="flex flex-col text-Default gap-4 pt-6 pb-4 text-sm font-base md:leading-5 tracking-normal">
          {tier.inclusions?.length > 0 &&
            tier.inclusions.map((name, i) => (
              <div className="flex flex-row justify-between" key={i}>
                <p className="flex flex-row pr-2">{name}</p>
                <div className="flex-shrink-0 w-5 h-5">
                  <ImageWrapper src="/icons/tick.svg" alt="Included" width={20} height={20} />
                </div>
              </div>
            ))}

          <div className="flex flex-row justify-between">
            <p>Participants</p>
            <div>
              {isCustom
                ? `${tier.minParticipants}${tier.maxParticipants ? `–${tier.maxParticipants}` : "+"}`
                : `Upto ${tier.maxParticipants || tier.minParticipants}`}
            </div>
          </div>
          {tier.note && <p className="text-xs text-gray-400 italic">{tier.note}</p>}
        </div>

        {isCustom && (
          <div
            className="flex items-center gap-3 mb-3 rounded-md border border-gray-200 bg-white p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => {
                onSelectCustomTier();
                onCustomCountChange(-1);
              }}
              disabled={isSelected && participantCount <= tier.minParticipants}
              className="w-8 h-8 rounded-md bg-gray-100 hover:bg-gray-200 disabled:opacity-40 flex items-center justify-center flex-shrink-0"
            >
              <Minus size={13} />
            </button>
            <span className="flex-1 text-center text-sm font-bold text-Default">
              {isSelected ? participantCount : tier.minParticipants} participants
            </span>
            <button
              type="button"
              onClick={() => {
                onSelectCustomTier();
                onCustomCountChange(1);
              }}
              disabled={isSelected && !!tier.maxParticipants && participantCount >= tier.maxParticipants}
              className="w-8 h-8 rounded-md bg-gray-100 hover:bg-gray-200 disabled:opacity-40 flex items-center justify-center flex-shrink-0"
            >
              <Plus size={13} />
            </button>
          </div>
        )}

        <hr />

        <div className="flex flex-col gap-2 mt-3">
          <button
            type="button"
            disabled={isBookingThisTier}
            className="w-full py-3 rounded-md bg-BluntRed text-white text-sm font-semibold
  flex items-center justify-center gap-2
  shadow-lg hover:shadow-xl
  hover:scale-[1.02] active:scale-[0.98]
  transition-all duration-200 relative overflow-hidden disabled:opacity-60"
            onClick={() => onBookNow(tierKey, tier)}
          >
            {!isBookingThisTier && <span className="absolute inset-0 animate-ping bg-red-800 opacity-20 rounded-md"></span>}
            <Zap size={16} className={isBookingThisTier ? "" : "animate-bounce"} />
            <span className="relative z-10">{isBookingThisTier ? "Please wait…" : "Book Now"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TemplePujaDetailPage({ puja }) {
  const router = useRouter();

  const [packageDoc, setPackageDoc] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingExtras, setLoadingExtras] = useState(true);

  const [selectedTierKey, setSelectedTierKey] = useState(null);
  const [participantCount, setParticipantCount] = useState(0);
  const [selectedSlotId, setSelectedSlotId] = useState(null);

  const [galleryIndex, setGalleryIndex] = useState(0);
  const [showCheckout, setShowCheckout] = useState(false);
  const [startingCheckoutTier, setStartingCheckoutTier] = useState(null);

  const fetchExtras = useCallback(async (templePujaId) => {
    setLoadingExtras(true);
    try {
      const [pkgRes, slotRes] = await Promise.all([
        GETREQUESTAPI(`${BACKEND_API_ROUTE}${TEMPLE_PUJA_PACKAGES_API}?templePujaId=${templePujaId}`),
        GETREQUESTAPI(`${BACKEND_API_ROUTE}${TEMPLE_PUJA_DATE_SLOTS_API}?templePujaId=${templePujaId}`),
      ]);
      if (pkgRes?.data?.statusCode === 200) {
        setPackageDoc((pkgRes.data.body || [])[0] || null);
      }
      if (slotRes?.data?.statusCode === 200) {
        setSlots(slotRes.data.body || []);
      }
    } catch (error) {
      console.error("Error loading Temple Puja package/slots:", error);
    } finally {
      setLoadingExtras(false);
    }
  }, []);

  useEffect(() => {
    if (puja?._id) fetchExtras(puja._id);
  }, [puja?._id, fetchExtras]);

  // Ascending by date & time, so devotees see the soonest Pooja first
  // regardless of the order the API returns them in.
  const sortedSlots = useMemo(
    () =>
      [...slots].sort(
        (a, b) =>
          parseISTSlotToMillis(a.date, a.timeSlot) - parseISTSlotToMillis(b.date, b.timeSlot)
      ),
    [slots]
  );

  // Default to the first bookable (not full, not already past) date/time
  // slot so the devotee only has to pick a package — they can still change
  // the slot manually below.
  useEffect(() => {
    if (selectedSlotId || sortedSlots.length === 0) return;
    const now = getNowInISTMillis();
    const firstAvailable = sortedSlots.find(
      (s) =>
        !(s.capacityTotal > 0 && s.capacityBooked >= s.capacityTotal) &&
        parseISTSlotToMillis(s.date, s.timeSlot) >= now
    );
    setSelectedSlotId((firstAvailable || sortedSlots[0])._id);
  }, [sortedSlots, selectedSlotId]);

  const media = puja?.media?.filter((m) => m?.mediaUrl) || [];

  const handleCustomCountChange = (delta) => {
    const custom = packageDoc?.custom;
    if (!custom) return;
    const min = custom.minParticipants;
    const max = custom.maxParticipants || 999;
    setParticipantCount((prev) => {
      const base = prev || min;
      const next = base + delta;
      return Math.min(max, Math.max(min, next));
    });
  };

  // Clicking "Book Now" on a package is the only entry point into booking:
  // silently ensure a (guest) session exists, then jump straight into the
  // inline checkout modal — same pattern as the regular Pooja flow's
  // handleBookNow, where OTP verification (if needed) happens inside the
  // checkout modal itself rather than gating access to it on the page.
  const handleBookNowOnPackage = async (tierKey, tier) => {
    if (!selectedSlotId) {
      toast.error("No dates are open for booking right now");
      return;
    }
    const count = tierKey === "custom" ? (participantCount || tier.minParticipants) : tier.minParticipants;
    setSelectedTierKey(tierKey);
    setParticipantCount(count);

    setStartingCheckoutTier(tierKey);
    try {
      if (!getCookieItem("accessToken")) {
        await handleGuestLoginFlow();
      }
      setShowCheckout(true);
    } finally {
      setStartingCheckoutTier(null);
    }
  };

  const amount = useMemo(() => {
    if (!selectedTierKey || !packageDoc) return 0;
    const tier = packageDoc[selectedTierKey];
    if (!tier) return 0;
    if (selectedTierKey === "custom") {
      const extra = Math.max(0, participantCount - tier.minParticipants);
      return tier.price + extra * tier.pricePerAdditionalParticipant;
    }
    return tier.price;
  }, [selectedTierKey, participantCount, packageDoc]);

  const selectedSlot = sortedSlots.find((s) => s._id === selectedSlotId);

  if (!puja) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="text-center">
          <Landmark size={40} className="text-red-100 mx-auto mb-4" />
          <h1 className="text-lg font-medium text-Default">Temple Pooja not found</h1>
          <p className="text-sm text-HeaderQuote mt-1.5">It may have been removed or the link is incorrect.</p>
        </div>
      </main>
    );
  }

  const seoData = {
    title: `${puja.name} | Book My Pooja`,
    description: puja.shortDescription || DEFAULT_SEO_VALUES.description,
    imageLink: media[0]?.mediaUrl || DEFAULT_SEO_VALUES.imageLink,
  };

  return (
    <>
      <Head>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content={seoData.imageLink} />
        <meta property="og:type" content="website" />
      </Head>

      <main className="min-h-screen bg-white pb-16">
        <div className="pt-10 md:pt-20 bg-white flex flex-col md:flex-row md:gap-12 lg:gap-16 xl:gap-20 px-0 md:px-20 lg:px-32 xl:px-35 2xl:px-44 3xl:px-56 4xl:px-64">
          {/* ── Left: sticky gallery ── */}
          <div className="w-full md:w-1/4 flex justify-center md:justify-end md:sticky md:top-36 md:h-fit md:self-start">
            <div className="flex flex-col w-full justify-start">
              <div className="flex px-6 md:px-0 xl:w-[444px] 2xl:w-[500px] 3xl:w-[550px] 4xl:w-[600px] flex-row justify-center lg:justify-start xl:justify-end bg-white overflow-hidden rounded-2xl">
                <div className="w-full h-[300px] xxs:h-[330px] xs:h-[420px] sm:h-[480px] lg:w-[400px] xl:w-[444px] lg:h-[464px] 2xl:w-[500px] 2xl:h-[530px] 3xl:w-[550px] 3xl:h-[590px] 4xl:w-[600px] 4xl:h-[650px] relative">
                  {media.length > 0 ? (
                    <>
                      <ImageWrapper
                        src={media[galleryIndex]?.mediaUrl}
                        alt={puja.name || "BookMyPooja"}
                        className="overflow-hidden object-cover border rounded-2xl"
                      />
                      {media.length > 1 && (
                        <>
                          <button
                            onClick={() => setGalleryIndex((i) => (i - 1 + media.length) % media.length)}
                            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow-md"
                          >
                            <ChevronLeft size={16} className="text-BluntRed" />
                          </button>
                          <button
                            onClick={() => setGalleryIndex((i) => (i + 1) % media.length)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow-md"
                          >
                            <ChevronRight size={16} className="text-BluntRed" />
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-50 border rounded-2xl">
                      <Landmark size={56} className="text-red-100" />
                    </div>
                  )}
                </div>
              </div>
              {media.length > 0 && (
                <div className="flex px-6 md:px-1 md:w-[444px] 2xl:w-[500px] 3xl:w-[550px] 4xl:w-[600px] right-0 flex-row gap-[10px] items-start justify-start overflow-x-auto scrollbar-hide pr-4 py-3">
                  {media.map((mediaLink, index) => (
                    <div
                      key={index}
                      className={`relative flex-none w-[76px] h-[80px] border rounded-lg overflow-hidden cursor-pointer transition-transform duration-200 ease-in-out hover:scale-105 bg-black ${index === galleryIndex ? "border-BluntRed border-2" : ""}`}
                      onClick={() => setGalleryIndex(index)}
                    >
                      <ImageWrapper
                        src={mediaLink.mediaUrl}
                        alt={puja.name}
                        fill
                        className="object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Right: content ── */}
          <div className="w-full md:w-2/4 md:ml-auto overflow-auto bg-white">
            <h4 className="hidden md:flex flex-row flex-wrap tracking-wide text-xs text-Default pb-4">
              <span
                onClick={() => router.push("/e-pooja/temple/pujas")}
                className="hover:cursor-pointer hover:text-BluntRed underline"
              >
                Temple Poojas
              </span>
              <span className="mx-1">/</span>
              <span className="text-BluntRed font-medium">{puja.name}</span>
            </h4>

            <div className="md:overflow-y-scroll md:scrollbar-hide">
              <h4 className="text-xl lg:text-4xl px-6 md:px-0 leading-8 lg:leading-tight tracking-wide text-Default font-medium lg:font-normal">
                {puja.name}
              </h4>

              {(puja.templeName || puja.templeLocation || puja.templeAddress) && (
                <div className="flex flex-col gap-1 px-6 md:px-0 pt-2 text-sm text-Default">
                  {puja.templeName && (
                    <p className="flex items-center gap-1.5">
                      <Landmark size={13} className="text-BluntRed flex-shrink-0" /> {puja.templeName}
                    </p>
                  )}
                  {puja.templeLocation && (
                    <p className="flex items-center gap-1.5">
                      <MapPin size={13} className="text-BluntRed flex-shrink-0" /> {puja.templeLocation}
                    </p>
                  )}
                  {puja.templeAddress && (
                    <p className="text-xs text-HeaderQuote pl-[21px]">{puja.templeAddress}</p>
                  )}
                </div>
              )}

              {puja.shortDescription && (
                <p className="flex text-sm lg:text-base px-6 md:px-0 text-Default leading-5 md:leading-6 tracking-normal py-2">
                  {puja.shortDescription}
                </p>
              )}

              {/* ── Date slot selection (shown first — pre-selects the first slot) ── */}
              <div className="px-6 md:px-0 pt-4 md:pt-6">
                <h3 className="text-base md:text-xl font-medium text-Default leading-7 tracking-wide">
                  Select Date &amp; Time
                </h3>
                <p className="text-xs text-HeaderQuote mt-1 mb-4">Choose from the available dates for this Pooja.</p>

                {loadingExtras ? (
                  <div className="flex justify-center py-10">
                    <div className="w-6 h-6 rounded-full border-4 border-gray-200 border-t-BluntRed animate-spin" />
                  </div>
                ) : sortedSlots.length > 0 ? (
                  <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 gap-3">
                    {sortedSlots.map((slot) => {
                      const isFull = slot.capacityTotal > 0 && slot.capacityBooked >= slot.capacityTotal;
                      const isPast = parseISTSlotToMillis(slot.date, slot.timeSlot) < getNowInISTMillis();
                      const isDisabled = isFull || isPast;
                      const isSelected = selectedSlotId === slot._id;
                      return (
                        <button
                          key={slot._id}
                          type="button"
                          disabled={isDisabled}
                          onClick={() => setSelectedSlotId(slot._id)}
                          className={`text-left border rounded-md p-3 transition-all ${
                            isDisabled
                              ? "border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed"
                              : isSelected
                                ? "border-BluntRed bg-red-50"
                                : "border-gray-200 bg-white hover:border-gray-300"
                          }`}
                        >
                          <p className={`text-xs font-medium flex items-center gap-1.5 ${isSelected ? "text-BluntRed" : "text-Default"}`}>
                            <Calendar size={11} /> {slot.date}
                          </p>
                          <p className="text-[11px] text-HeaderQuote flex items-center gap-1.5 mt-1">
                            <Clock size={10} /> {slot.timeSlotText || slot.timeSlot}
                          </p>
                          {isFull && <p className="text-[10px] font-medium text-cancelled-color mt-1.5">Fully booked</p>}
                          {!isFull && isPast && <p className="text-[10px] font-medium text-gray-400 mt-1.5">Date passed</p>}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-HeaderQuote py-6">No dates are open for booking right now.</p>
                )}
              </div>

              {/* ── Packages Section ── */}
              <div className="px-6 md:px-0">
                <h3 className="text-base md:text-xl font-medium text-Default pt-4 md:pt-6 leading-7 tracking-wide">
                  Pooja Packages
                </h3>

                {loadingExtras ? (
                  <div className="flex justify-center py-12">
                    <div className="w-6 h-6 rounded-full border-4 border-gray-200 border-t-BluntRed animate-spin" />
                  </div>
                ) : packageDoc ? (
                  <div className="flex overflow-x-auto scrollbar-hide py-12">
                    {VISIBLE_PARTICIPANT_TIERS.filter((k) => tierExists(packageDoc[k])).map((tierKey) => (
                      <TemplePujaPackageCard
                        key={tierKey}
                        tierKey={tierKey}
                        tier={packageDoc[tierKey]}
                        isSelected={selectedTierKey === tierKey}
                        participantCount={participantCount}
                        isBookingThisTier={startingCheckoutTier === tierKey}
                        onSelectCustomTier={() => setSelectedTierKey("custom")}
                        onCustomCountChange={handleCustomCountChange}
                        onBookNow={handleBookNowOnPackage}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-HeaderQuote py-8">Packages for this Pooja are not available yet.</p>
                )}
              </div>

              <hr className="border-solid border-Default opacity-30" />

              {/* ── Our Promise ── */}
              <div className="flex flex-col gap-5 md:pt-6">
                <h3 className="text-base md:text-xl px-6 md:px-0 font-medium text-Default pt-4 leading-7 tracking-wide">
                  Our Promise
                </h3>
                <div className="flex flex-row overflow-x-auto px-6 md:px-0 text-[10px] leading-4 text-center text-Default scrollbar-hide gap-4 pb-8">
                  <div className="flex flex-col w-24">
                    <div className="flex-none w-16 h-16 border border-[#8A1C2333] rounded-full p-4 mx-3 flex items-center justify-center">
                      <ImageWrapper src="/icons/Group7570.svg" alt="Experienced priests" width={32} height={32} />
                    </div>
                    <p className="py-3">Experienced Pandits</p>
                  </div>
                  <div className="flex flex-col w-24">
                    <div className="flex-none w-16 h-16 border border-[#8A1C2333] rounded-full p-4 mx-3 flex items-center justify-center">
                      <ImageWrapper src="/icons/paper.svg" alt="Paper icon" width={32} height={32} />
                    </div>
                    <p className="py-3">Vedic Standards & Procedures</p>
                  </div>
                  <div className="flex flex-col w-24">
                    <div className="flex-none w-16 h-16 border border-[#8A1C2333] rounded-full p-4 mx-3 flex items-center justify-center">
                      <ImageWrapper src="/icons/star.svg" alt="Great Experience" width={32} height={32} />
                    </div>
                    <p className="py-3">100% Puja Benefits</p>
                  </div>
                  <div className="flex flex-col w-24">
                    <div className="flex-none w-16 h-16 border border-[#8A1C2333] rounded-full p-4 mx-3 flex items-center justify-center">
                      <ImageWrapper src="/icons/Group7565.svg" alt="High Quality" width={32} height={32} />
                    </div>
                    <p className="py-3">High Quality Samgri</p>
                  </div>
                  <div className="flex flex-col w-24">
                    <div className="flex-none w-16 h-16 border border-[#8A1C2333] rounded-full p-4 mx-3 flex items-center justify-center">
                      <ImageWrapper src="/icons/headphone-dummy.svg" alt="Professional" width={32} height={32} />
                    </div>
                    <p className="py-3">Professional Guidance and Support</p>
                  </div>
                  <div className="flex flex-col w-24">
                    <div className="flex-none w-16 h-16 border border-[#8A1C2333] rounded-full p-4 mx-3 flex items-center justify-center">
                      <ImageWrapper src="/icons/star.svg" alt="Great Experience" width={32} height={32} />
                    </div>
                    <p className="py-3">Guaranteed Punctuality</p>
                  </div>
                </div>
              </div>

              <hr className="border-solid border-Default opacity-30" />

              {puja.description && (
                <div className="px-6 md:px-0 md:pb-8 text-justify">
                  <h3>{parse(parsePoojaDescriptionRawHTML(puja.description))}</h3>
                </div>
              )}

              {puja.additionalInfo && (
                <div className="px-6 md:px-0 pb-8">
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-Default mb-2 flex items-center gap-1.5">
                      <Info size={14} className="text-BluntRed" /> Additional Information
                    </h3>
                    <div className="text-sm text-Default">
                      {parse(parsePoojaDescriptionRawHTML(puja.additionalInfo))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {showCheckout && (
        <TemplePujaCheckout
          templePujaId={puja._id}
          templePujaName={puja.name}
          templePujaPackageId={packageDoc?._id}
          packageType={PACKAGE_TYPE_MAP[selectedTierKey]}
          participantCount={participantCount}
          templePujaDateSlotId={selectedSlotId}
          bookingDate={selectedSlot?.date}
          bookingTimeSlotText={selectedSlot?.timeSlotText || selectedSlot?.timeSlot}
          amount={amount}
          onClose={() => setShowCheckout(false)}
        />
      )}
    </>
  );
}

export async function getServerSideProps(context) {
  const { slug } = context.params;
  try {
    const url = `${BACKEND_API_ROUTE}${TEMPLE_PUJA_API}/${slug}`;
    const response = await GETREQUESTAPI(url);
    if (response?.status === 200 && response?.data?.statusCode === 200 && response.data.body) {
      return { props: { puja: response.data.body } };
    }
  } catch (error) {
    console.error("Error fetching Temple Puja details:", error.message);
  }
  return { props: { puja: null } };
}
